import React, { useState, useEffect } from "react";
import {
  Popconfirm,
  Modal,
  Button,
  Table,
  Form,
  Input,
  message,
  Upload,
  Tooltip,
} from "antd";
import axios from "axios";
import DefaultLayout from "../components/DefaultLayout";
import CloseButton from "../components/CloseButton";
import Swal from "sweetalert2";
import config from "../config";
import { DeleteOutlined, EditOutlined, UploadOutlined } from "@ant-design/icons";
import '@fortawesome/fontawesome-free/css/all.min.css';
import { Select } from 'antd';
import { QuestionCircleOutlined } from "@ant-design/icons";


const ProductIcon = () => {
  const apiBaseUrl = config.REACT_APP_API_BASE_URL;

  const [iconData, setIconData] = useState([]);
  const [filteredIconData, setFilteredIconData] = useState([]);
  const [popupModal, setPopupModal] = useState(false);
  const [editIcon, setEditIcon] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const getAllIcons = async () => {
    try {
      const { data } = await axios.get(`${apiBaseUrl}/api/icons/get`);
      setIconData(data);
      setFilteredIconData(data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getAllIcons();
  }, []);

  const handleDelete = async (record) => {
    try {
      await axios.post(`${apiBaseUrl}/api/icons/delete`, {
        iconId: record._id,
      });
      Swal.fire({
        title: "Deleted",
        text: "Icon deleted successfully",
        toast: true,
        icon: "success",
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
      getAllIcons();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Something went wrong",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };

  const columns = [
    {
      title: "No",
      render: (text, record, recordIndex) => (
        <span>{(pagination.current - 1) * pagination.pageSize + recordIndex + 1}</span>
      ),
    },
    {
      title: "Icon Name",
      dataIndex: "iconName",
      sorter: (a, b) => a.iconName.localeCompare(b.iconName),
      showSorterTooltip: false,
    },
    // {
    //   title: "FontAwesome Icon",
    //   dataIndex: "faIcon",
    //   render: (text) => {
    //     return text ? <i className={text} style={{ fontSize: 20 }}></i> : "N/A";
    //   }
    // },
    {
      title: "FontAwesome Icon",
      dataIndex: "faIcon",
      render: (text, record) => {
        return text ? (
          <i
            className={text}
            style={{ fontSize: 20, color: record.iconColor || "black" }}
          ></i>
        ) : (
          "N/A"
        );
      },
    },
    {
      title: "Actions",
      render: (text, record) => (
        <div className="action-icons">
          <EditOutlined
            style={{ cursor: "pointer", fontSize: 22, marginRight: 10 }}
            onClick={() => {
              setEditIcon(record);
              form.setFieldsValue({
                iconName: record.iconName,
                faIcon: record.faIcon,
                iconColor: record.iconColor
              });
              setPopupModal(true);
            }}
          />
          <Popconfirm
            title="Are you sure you want to delete this icon?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
            placement="left"
          >
            <DeleteOutlined style={{ cursor: "pointer", fontSize: 22, color: "red" }} />
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleCancel = () => {
    if (!loading) {
      setPopupModal(false);
      setEditIcon(null);
      form.resetFields();
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);

    const payload = {
      iconName: values.iconName,
      iconColor: values.iconColor,
      faIcon: values.faIcon,
    };

    console.log("Submitting icon data:", payload);

    try {
      if (editIcon) {
        // For update
        payload.iconId = editIcon._id;
        await axios.put(`${apiBaseUrl}/api/icons/update`, payload);
      } else {
        // For create
        await axios.post(`${apiBaseUrl}/api/icons/create`, payload);
      }

      message.success("Icon saved successfully");
      getAllIcons();
      handleCancel();
    } catch (error) {
      console.error("Error:", error);
      message.error("Operation failed: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (searchValue) => {
    const lowerSearch = searchValue.toLowerCase();

    if (searchValue) {
      const filtered = iconData.filter(record =>
        (record.iconName.toLowerCase().includes(lowerSearch))
      );
      setFilteredIconData(filtered);
    } else {
      setFilteredIconData(iconData);
    }
  };

  return (
    <DefaultLayout searchPlaceholder="Icon Name" onSearch={handleSearch}>
      {/* Main content wrapper for blur effect */}
      <div className={`product-icon-page-wrapper${popupModal ? ' blur-background' : ''}`}>
      <div className="page-header"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <h2 className="page-title">Product Icons</h2>
          <Button type="primary" onClick={() => setPopupModal(true)}>
            Add Icon
          </Button>
        </div>
        <Table 
          columns={columns} 
          dataSource={filteredIconData} 
          rowKey="_id" 
          bordered
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            pageSizeOptions: ['10', '20', '50', '100'],
            showSizeChanger: filteredIconData.length > 10,
            showTotal: (total, range) => `Showing ${range[0]}–${range[1]} of ${total} items`,
            onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
          }}
        />
      </div>
      {/* Modal is rendered outside the blurred wrapper so it is not blurred */}
      <Modal
        title={
          <div>
            {editIcon ? "Edit Icon" : "Add New Icon"}
            <CloseButton onClick={handleCancel} />
          </div>
        }
        open={popupModal}
        onCancel={handleCancel}
        footer={false}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="iconName"
            label="Icon Name"
            rules={[{ required: true, message: "Please enter icon name" }]}
          >
            <Input />
          </Form.Item>

          {/* <Form.Item
            name="faIcon"
            label="FontAwesome Icon Class"
            rules={[{ required: true, message: "Please enter icon class" }]}
          >
            <Input placeholder="e.g. fa-solid fa-burger, fa-solid fa-location-pin" />
          </Form.Item> */}

          <Form.Item
            name="faIcon"
            label="FontAwesome Icon"
            rules={[{ required: true, message: 'Please select an icon' }]}
          >
            <Select placeholder="Select an icon">
              <Select.Option value="fa-solid fa-thumbs-up"><i className="fa-solid fa-thumbs-up" style={{ fontSize: 16 }}/> fa-solid fa-thumbs-up</Select.Option>
              <Select.Option value="fa-solid fa-leaf"><i className="fa-solid fa-leaf" style={{ fontSize: 16 }}/> fa-solid fa-leaf</Select.Option>
              <Select.Option value="fas fa-concierge-bell"><i className="fas fa-concierge-bell" style={{ fontSize: 16 }}/> fas fa-concierge-bell</Select.Option>
              <Select.Option value="fa-solid fa-fire"><i className="fa-solid fa-fire" style={{ fontSize: 16 }}/> fa-solid fa-fire</Select.Option>
              <Select.Option value="fa-solid fa-burger"><i className="fa-solid fa-burger" style={{ fontSize: 16 }}/> fa-solid fa-burger</Select.Option>
              <Select.Option value="fa-solid fa-star"><i className="fa-solid fa-star" style={{ fontSize: 16 }}/> fa-solid fa-star</Select.Option>
              <Select.Option value="fa-solid fa-pepper-hot"><i className="fa-solid fa-pepper-hot" style={{ fontSize: 16 }}/> fa-solid fa-pepper-hot</Select.Option> 
              <Select.Option value="fa-solid fa-wheat-awn"><i className="fa-solid fa-wheat-awn" style={{ fontSize: 16 }}/> fa-solid fa-wheat-awn</Select.Option>           
              <Select.Option value="fa-solid fa-clock"><i className="fa-solid fa-clock" style={{ fontSize: 16 }}/> fa-solid fa-clock</Select.Option>           

            </Select>
          </Form.Item>

          {/* <Form.Item
            name="iconColor"
            label="Icon Color"
            rules={[{ required: true, message: "Please enter color code" }]}
          >
            <Input />
          </Form.Item> */}

          <Form.Item
            name="iconColor"
            label={
                <span>
                  Icon Color
                  <Tooltip title="Click the color to select a color for this icon">
                    <QuestionCircleOutlined
                      style={{
                        marginLeft: 8,
                        color: "#999",
                        fontSize: "1.5em",
                      }}
                    />
                  </Tooltip>
                </span>
              }
            initialValue="#000000"
          >
            <Input
              type="color"
              style={{
                width: "40px",
                height: "40px",
                padding: 0,
                border: "none",
                background: "none",
                cursor: "pointer",
                borderRadius: "6px", 
              }}
            />
          </Form.Item>
          <div className="d-flex justify-content-end ant-modal-footer">
            <Button type="primary" htmlType="submit" loading={loading}>
              Save
            </Button>
          </div>
        </Form>
      </Modal>
    </DefaultLayout>
  );
};

export default ProductIcon;
